import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Upload,
  FileSpreadsheet,
  Users,
  Trophy,
  Download,
  LogOut,
  TrendingUp,
  TrendingDown,
  Minus,
  Building2,
  UserPlus,
  UserMinus,
  Award,
  Calendar,
  BarChart3,
  Loader2,
} from 'lucide-react';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

// Types
interface CompanyStats {
  totalUsers: number;
  profilesCreated: number;
  activeUsers: number;
  totalLiters: number;
  loggedCount: number;
  skippedCount: number;
}

interface DepartmentStats {
  department: string;
  userCount: number;
  totalLiters: number;
  avgLitersPerUser: number;
}

interface TopPerformer {
  userId: string;
  name: string;
  department: string;
  totalLiters: number;
  logCount: number;
}

interface WhitelistedUser {
  id: string;
  email: string;
  department: string | null;
  status: string | null;
  createdAt: string;
}

interface BadgeEarned {
  userId: string;
  badgeName: string;
  earnedAt: string;
}

// API functions
const fetchDailyStats = async (): Promise<CompanyStats> => {
  const res = await fetch('/api/admin/hr/stats/daily', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch daily stats');
  return res.json();
};

const fetchWeeklyStats = async (): Promise<CompanyStats> => {
  const res = await fetch('/api/admin/hr/stats/weekly', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch weekly stats');
  return res.json();
};

const fetchCampaignStats = async () => {
  const res = await fetch('/api/admin/hr/stats/campaign', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch campaign stats');
  return res.json();
};

const fetchDepartments = async (): Promise<DepartmentStats[]> => {
  const res = await fetch('/api/admin/hr/departments', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch departments');
  return res.json();
};

const fetchTopPerformers = async (): Promise<TopPerformer[]> => {
  const res = await fetch('/api/admin/hr/top-performers?limit=10', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch top performers');
  return res.json();
};

const fetchBadgesEarned = async (): Promise<BadgeEarned[]> => {
  const res = await fetch('/api/admin/hr/badges-earned', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch badges');
  return res.json();
};

const fetchWhitelistedUsers = async (): Promise<WhitelistedUser[]> => {
  const res = await fetch('/api/admin/hr/whitelisted-users', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch whitelisted users');
  return res.json();
};

const TrendIndicator = ({ value, suffix = '%' }: { value: number; suffix?: string }) => {
  if (value > 0) {
    return (
      <span className="flex items-center gap-1 text-green-600 text-sm">
        <TrendingUp className="w-4 h-4" />+{value.toFixed(1)}
        {suffix}
      </span>
    );
  } else if (value < 0) {
    return (
      <span className="flex items-center gap-1 text-red-500 text-sm">
        <TrendingDown className="w-4 h-4" />
        {value.toFixed(1)}
        {suffix}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-muted-foreground text-sm">
      <Minus className="w-4 h-4" />0{suffix}
    </span>
  );
};

const HRPanel = () => {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserDept, setNewUserDept] = useState('');
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);

  // Queries
  const { data: dailyStats, isLoading: loadingDaily } = useQuery({
    queryKey: ['hr-stats-daily'],
    queryFn: fetchDailyStats,
  });

  const { data: weeklyStats, isLoading: loadingWeekly } = useQuery({
    queryKey: ['hr-stats-weekly'],
    queryFn: fetchWeeklyStats,
  });

  const { data: campaignStats } = useQuery({
    queryKey: ['hr-stats-campaign'],
    queryFn: fetchCampaignStats,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['hr-departments'],
    queryFn: fetchDepartments,
  });

  const { data: topPerformers = [] } = useQuery({
    queryKey: ['hr-top-performers'],
    queryFn: fetchTopPerformers,
  });

  const { data: badgesEarned = [] } = useQuery({
    queryKey: ['hr-badges-earned'],
    queryFn: fetchBadgesEarned,
  });

  const { data: whitelistedUsers = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['hr-whitelisted-users'],
    queryFn: fetchWhitelistedUsers,
  });

  // Mutations
  const addUserMutation = useMutation({
    mutationFn: async (data: { email: string; department?: string }) => {
      const res = await fetch('/api/admin/hr/whitelisted-users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to add user');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-whitelisted-users'] });
      toast({ title: 'User added', description: 'User has been whitelisted successfully.' });
      setNewUserEmail('');
      setNewUserDept('');
      setIsAddUserOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/admin/hr/whitelisted-users/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete user');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-whitelisted-users'] });
      toast({ title: 'User removed', description: 'User has been removed from whitelist.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to remove user', variant: 'destructive' });
    },
  });

  const bulkUploadMutation = useMutation({
    mutationFn: async (users: { email: string; department?: string }[]) => {
      const res = await fetch('/api/admin/hr/bulk-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ users }),
      });
      if (!res.ok) throw new Error('Failed to upload users');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hr-whitelisted-users'] });
      toast({
        title: 'Upload complete',
        description: `Added ${data.created} users. ${data.skipped} already existed.`,
      });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to upload users', variant: 'destructive' });
    },
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').slice(1); // Skip header
      const users = lines
        .filter((line) => line.trim())
        .map((line) => {
          const [email, department] = line.split(',').map((s) => s.trim());
          return { email, department: department || undefined };
        })
        .filter((u) => u.email);

      if (users.length > 0) {
        bulkUploadMutation.mutate(users);
      } else {
        toast({
          title: 'Error',
          description: 'No valid users found in CSV',
          variant: 'destructive',
        });
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadReport = async () => {
    try {
      const res = await fetch('/api/admin/hr/reports/download', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to download report');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `swyg-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({ title: 'Report downloaded', description: 'Your report has been downloaded.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to download report', variant: 'destructive' });
    }
  };

  // Group badges by name for display
  const badgeCounts = badgesEarned.reduce(
    (acc, badge) => {
      acc[badge.badgeName] = (acc[badge.badgeName] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const calculateChange = () => {
    if (!campaignStats?.before || !campaignStats?.after) return 0;
    const before = campaignStats.before.totalLiters / Math.max(campaignStats.before.activeUsers, 1);
    const after = campaignStats.after.totalLiters / Math.max(campaignStats.after.activeUsers, 1);
    if (before === 0) return 0;
    return ((after - before) / before) * 100;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">HR Admin Panel</h1>
            <p className="text-muted-foreground">Manage users, reports, and awards</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setLocation('/logout')}
            className="text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <Tabs defaultValue="reports" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full bg-lilac-100">
            <TabsTrigger value="reports" className="text-xs sm:text-sm">
              <BarChart3 className="w-4 h-4 mr-1 hidden sm:inline" />
              Reports
            </TabsTrigger>
            <TabsTrigger value="users" className="text-xs sm:text-sm">
              <Users className="w-4 h-4 mr-1 hidden sm:inline" />
              Users
            </TabsTrigger>
            <TabsTrigger value="awards" className="text-xs sm:text-sm">
              <Trophy className="w-4 h-4 mr-1 hidden sm:inline" />
              Awards
            </TabsTrigger>
            <TabsTrigger value="upload" className="text-xs sm:text-sm">
              <Upload className="w-4 h-4 mr-1 hidden sm:inline" />
              Upload
            </TabsTrigger>
          </TabsList>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <Tabs defaultValue="daily" className="space-y-4">
              <TabsList className="bg-lilac-200">
                <TabsTrigger value="daily">
                  <Calendar className="w-4 h-4 mr-1" />
                  Daily
                </TabsTrigger>
                <TabsTrigger value="weekly">Weekly</TabsTrigger>
                <TabsTrigger value="campaign">Campaign</TabsTrigger>
              </TabsList>

              {/* Daily Report */}
              <TabsContent value="daily" className="space-y-4">
                <Card className="p-6 bg-gradient-to-br from-lilac-100 to-purple-50">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-primary" />
                    Daily Aggregate Report
                    {loadingDaily && <Loader2 className="w-4 h-4 animate-spin" />}
                  </h3>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {dailyStats?.profilesCreated || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Profiles Created</div>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {dailyStats?.activeUsers || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Active Users</div>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {dailyStats?.totalLiters?.toFixed(1) || 0}L
                      </div>
                      <div className="text-xs text-muted-foreground">Total Liters Today</div>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {dailyStats?.loggedCount || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Logged</div>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-red-500">
                        {dailyStats?.skippedCount || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Skipped</div>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {dailyStats?.totalUsers || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Users</div>
                    </div>
                  </div>
                </Card>

                {/* Department-wise */}
                <Card className="p-6">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    Department-wise Breakdown
                  </h3>
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {departments.map((dept, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-lilac-50 rounded-lg"
                        >
                          <div>
                            <div className="font-medium text-foreground">{dept.department}</div>
                            <div className="text-xs text-muted-foreground">
                              {dept.userCount} users
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-purple-600">
                              {dept.avgLitersPerUser.toFixed(1)}L avg
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {dept.totalLiters.toFixed(1)}L total
                            </div>
                          </div>
                        </div>
                      ))}
                      {departments.length === 0 && (
                        <div className="text-center text-muted-foreground py-8">
                          No department data available
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </Card>

                {/* Top Performers */}
                <Card className="p-6">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    Top Performers (Last 30 Days)
                  </h3>
                  <div className="space-y-3">
                    {topPerformers.slice(0, 5).map((performer, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-4 p-3 bg-gradient-to-r from-yellow-50 to-lilac-50 rounded-lg"
                      >
                        <div className="w-8 h-8 rounded-full bg-yellow-200 flex items-center justify-center font-bold text-yellow-700">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-foreground">{performer.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {performer.department} • {performer.logCount} logs
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-purple-600">
                            {performer.totalLiters.toFixed(1)}L
                          </div>
                        </div>
                      </div>
                    ))}
                    {topPerformers.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        No performer data available
                      </div>
                    )}
                  </div>
                </Card>

                {/* Badges Earned */}
                <Card className="p-6">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" />
                    Total Badges Earned Today
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-lilac-100 rounded-xl">
                      <div className="text-3xl mb-2">💧</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {badgeCounts['First Drop'] || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">First Drop</div>
                    </div>
                    <div className="text-center p-4 bg-lilac-100 rounded-xl">
                      <div className="text-3xl mb-2">⚡</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {badgeCounts['Week Warrior'] || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Week Warrior</div>
                    </div>
                    <div className="text-center p-4 bg-lilac-100 rounded-xl">
                      <div className="text-3xl mb-2">🏆</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {badgeCounts['Hydration Hero'] || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Hydration Hero</div>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              {/* Weekly Report */}
              <TabsContent value="weekly" className="space-y-4">
                <Card className="p-6 bg-gradient-to-br from-lilac-100 to-purple-50">
                  <h3 className="font-semibold text-foreground mb-4">
                    Weekly Summary
                    {loadingWeekly && <Loader2 className="w-4 h-4 animate-spin ml-2 inline" />}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {weeklyStats?.profilesCreated || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">New Profiles</div>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {weeklyStats?.activeUsers || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Active Users</div>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {weeklyStats?.totalLiters?.toFixed(1) || 0}L
                      </div>
                      <div className="text-xs text-muted-foreground">Total Liters</div>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {weeklyStats?.loggedCount || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Logs</div>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              {/* Campaign Report */}
              <TabsContent value="campaign" className="space-y-4">
                <Card className="p-6 bg-gradient-to-br from-lilac-100 to-purple-50">
                  <h3 className="font-semibold text-foreground mb-4">
                    Campaign Before/After Analysis
                  </h3>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-xs text-muted-foreground mb-2">Before</div>
                      <div className="text-2xl font-bold text-deep-600">
                        {campaignStats?.before
                          ? (
                              campaignStats.before.totalLiters /
                              Math.max(campaignStats.before.activeUsers, 1)
                            ).toFixed(1)
                          : 0}
                        L
                      </div>
                      <div className="text-xs text-muted-foreground">Avg/User</div>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-xs text-muted-foreground mb-2">After</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {campaignStats?.after
                          ? (
                              campaignStats.after.totalLiters /
                              Math.max(campaignStats.after.activeUsers, 1)
                            ).toFixed(1)
                          : 0}
                        L
                      </div>
                      <div className="text-xs text-muted-foreground">Avg/User</div>
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 text-center">
                      <div className="text-xs text-muted-foreground mb-2">Change</div>
                      <TrendIndicator value={calculateChange()} />
                    </div>
                  </div>

                  <h4 className="font-medium text-foreground mb-3">Top Departments</h4>
                  <div className="space-y-2">
                    {departments.slice(0, 3).map((dept, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-white/40 rounded-lg"
                      >
                        <span className="text-sm font-medium">
                          {index + 1}. {dept.department}
                        </span>
                        <span className="text-sm text-purple-600">
                          {dept.totalLiters.toFixed(1)}L total
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Download Button */}
            <Button
              onClick={handleDownloadReport}
              className="w-full bg-purple-500 hover:bg-purple-600"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Full Report
            </Button>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card className="p-6">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Whitelisted Users
                {loadingUsers && <Loader2 className="w-4 h-4 animate-spin" />}
              </h3>
              <ScrollArea className="h-64 mb-4">
                <div className="space-y-3">
                  {whitelistedUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-lilac-50 rounded-lg"
                    >
                      <div>
                        <div className="font-medium text-foreground text-sm">{user.email}</div>
                        <div className="text-xs text-muted-foreground">
                          {user.department || 'No department'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            user.status === 'active' || user.status === 'registered'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {user.status || 'pending'}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => deleteUserMutation.mutate(user.id)}
                          disabled={deleteUserMutation.isPending}
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {whitelistedUsers.length === 0 && !loadingUsers && (
                    <div className="text-center text-muted-foreground py-8">
                      No whitelisted users yet
                    </div>
                  )}
                </div>
              </ScrollArea>

              <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full" variant="outline">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Whitelisted User</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        placeholder="user@company.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="department">Department (optional)</Label>
                      <Input
                        id="department"
                        value={newUserDept}
                        onChange={(e) => setNewUserDept(e.target.value)}
                        placeholder="Engineering"
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={() =>
                        addUserMutation.mutate({
                          email: newUserEmail,
                          department: newUserDept || undefined,
                        })
                      }
                      disabled={!newUserEmail || addUserMutation.isPending}
                    >
                      {addUserMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      Add User
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </Card>
          </TabsContent>

          {/* Awards Tab */}
          <TabsContent value="awards" className="space-y-4">
            <Card className="p-6">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Awards & Recognition
              </h3>

              <div className="space-y-6">
                {/* Best Department */}
                <div className="p-4 bg-gradient-to-r from-yellow-50 to-lilac-50 rounded-xl">
                  <Label className="font-medium text-foreground">Best Department</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Department with highest total consumption
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 bg-white rounded-lg text-center">
                      <div className="text-2xl mb-1">🏢</div>
                      <div className="font-semibold text-foreground">
                        {departments[0]?.department || 'No data'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {departments[0]?.totalLiters?.toFixed(1) || 0}L total
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top 3 Performers */}
                <div className="p-4 bg-lilac-50 rounded-xl">
                  <Label className="font-medium text-foreground">Top 3 Performers</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Based on total water consumption
                  </p>
                  <div className="space-y-2">
                    {topPerformers.slice(0, 3).map((performer, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 bg-white rounded-lg">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                            index === 0
                              ? 'bg-yellow-300 text-yellow-800'
                              : index === 1
                                ? 'bg-gray-200 text-gray-700'
                                : 'bg-orange-200 text-orange-800'
                          }`}
                        >
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm">{performer.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {performer.department}
                          </div>
                        </div>
                        <Award
                          className={`w-5 h-5 ${
                            index === 0
                              ? 'text-yellow-500'
                              : index === 1
                                ? 'text-gray-400'
                                : 'text-orange-400'
                          }`}
                        />
                      </div>
                    ))}
                    {topPerformers.length === 0 && (
                      <div className="text-center text-muted-foreground py-4">
                        No performer data available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Bulk Upload Tab */}
          <TabsContent value="upload" className="space-y-4">
            <Card className="p-6">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-primary" />
                Bulk Upload User Email IDs
              </h3>

              <div
                className="border-2 border-dashed border-lilac-300 rounded-xl p-8 text-center mb-4 cursor-pointer hover:border-purple-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {bulkUploadMutation.isPending ? (
                  <Loader2 className="w-12 h-12 text-purple-500 mx-auto mb-4 animate-spin" />
                ) : (
                  <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                )}
                <p className="text-sm text-muted-foreground mb-2">
                  Drag and drop a CSV file or click to browse
                </p>
                <p className="text-xs text-muted-foreground">
                  CSV should contain columns: email, department
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <Button variant="outline" className="mt-4" disabled={bulkUploadMutation.isPending}>
                  Select File
                </Button>
              </div>

              <div className="p-4 bg-lilac-50 rounded-lg">
                <h4 className="font-medium text-sm text-foreground mb-2">CSV Format Example:</h4>
                <code className="text-xs text-muted-foreground block bg-white p-2 rounded">
                  email,department
                  <br />
                  john@company.com,Engineering
                  <br />
                  jane@company.com,Marketing
                </code>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default HRPanel;
